import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import Spinner from "react-bootstrap/Spinner";
import { IconSparkles, IconWorld } from "@tabler/icons-react";
import ReactGA from "react-ga4";
import { motion, AnimatePresence } from "framer-motion";
import { useHistory } from "react-router-dom";

import headerStyles from "../../../theme/header.module.css";
import styles from "./styles.module.css";

import AiApi from "../../../api/ai";
import SettingsApi from "../../../api/settings";
import isBlockedDomain from "../../../utils/domains";
import { markdownToPlainText } from "../../../utils/markdown";
import { useAppStore } from "../../../store/app";

const stepVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.3, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: { duration: 0.2, ease: "easeIn" },
  },
};

export default function Onboarding() {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const history = useHistory();

  const [formError, setFormError] = React.useState<string | null>(null);

  const { user, settings, setOnboarding, setOnboarded, setSettings, setTrained } =
    useAppStore((state) => state);

  const { website, companyName, aboutCompany, loading, step, error } =
    settings.onboarding;

  React.useEffect(() => {
    if (step === 0) {
      setOnboarding({ step: 1 });
    }
  }, [step, setOnboarding]);

  React.useEffect(() => {
    if (step !== 2) return;
    if (companyName.trim()) return;
    const fallbackName = settings.company?.name || "";
    if (fallbackName.trim()) {
      setOnboarding({ companyName: fallbackName });
    }
  }, [step, companyName, settings.company?.name, setOnboarding]);

  const mutateSingleCrawling = useMutation({
    mutationFn: AiApi.singleCrawling,
    onSuccess: async (data) => {
      if (data && data?.success) {
        setOnboarding({ fetchingWebsite: true });
        await queryClient.invalidateQueries({ queryKey: ["getAiStatus"] });
        await new Promise((resolve) => setTimeout(resolve, 1500));
        const crawledCompanyName =
          typeof data.companyName === "string" && data.companyName.trim()
            ? data.companyName.trim()
            : settings.company?.name || "";
        const crawledSummary = markdownToPlainText(data.summary);

        setOnboarding({
          teachingAgent: true,
          loading: false,
          step: 2,
          companyName: crawledCompanyName,
          aboutCompany: crawledSummary,
        });

        // @ts-ignore Google analytics custom event
        if (window.GA_INITIALIZED) {
          ReactGA.event("onboarding_website_submitted", {
            step: "website",
            flow: "signup",
          });
        }
      } else {
        setFormError("ai.onboarding.error");
        setOnboarding({
          loading: false,
          teachingAgent: false,
          creatingAgent: false,
          fetchingWebsite: false,
          error: true,
          message: "",
        });
      }
    },
    onError: () => {
      setFormError("ai.onboarding.error");
      setOnboarding({
        loading: false,
        teachingAgent: false,
        creatingAgent: false,
        fetchingWebsite: false,
        error: true,
        message: "",
      });
    },
  });

  const mutateCreateSystemPrompt = useMutation({
    mutationFn: AiApi.createSystemPrompt,
    onSuccess: async (data) => {
      if (data && data?.success) {
        setOnboarding({ creatingAgent: true });

        // Meta pixel sign up event
        if ((window as any).fbq) {
          (window as any).fbq("track", "CompleteRegistration");
        }
        // @ts-ignore Google analytics custom event
        if (window.GA_INITIALIZED) {
          ReactGA.event("onboarding_agent_created", {
            step: "agent_configuration",
            flow: "signup",
            agent_type: "general-purpose",
          });
        }

        return setTimeout(() => {
          void handleSkip(true);
        }, 1500);
      } else {
        setFormError("ai.onboarding.error");
        setOnboarding({
          loading: false,
          teachingAgent: false,
          creatingAgent: false,
          fetchingWebsite: true,
          error: true,
          message: "",
        });
      }
    },
    onError: () => {
      setFormError("ai.onboarding.error");
      setOnboarding({
        loading: false,
        teachingAgent: false,
        creatingAgent: false,
        fetchingWebsite: true,
        error: true,
        message: "",
      });
    },
  });

  const handleForm = async (e: React.FormEvent) => {
    e.preventDefault();
    switch (step) {
      case 1: {
        const blockedDomain = isBlockedDomain(website);
        if (blockedDomain) {
          return setFormError("common.invalidDomain");
        }
        setFormError(null);
        setOnboarding({
          loading: true,
          teachingAgent: false,
          creatingAgent: false,
          fetchingWebsite: false,
          error: false,
          message: "",
        });

        return mutateSingleCrawling.mutate({
          url: website,
          companyId: user.company.id,
          async: true,
        });
      }
      case 2: {
        const cleanCompanyName = companyName.trim();
        const cleanAboutCompany = aboutCompany.trim();
        if (!cleanCompanyName) {
          return setFormError("ai.onboarding.companyName.required");
        }
        if (!cleanAboutCompany) {
          return setFormError("ai.onboarding.aboutCompany.required");
        }
        setFormError(null);
        setOnboarding({ loading: true });
        return mutateCreateSystemPrompt.mutate({
          companyId: user.company.id,
          companyName: cleanCompanyName,
          aboutCompany: cleanAboutCompany,
          locale: user.language,
        });
      }
      default:
        return;
    }
  };

  const handleSkip = async (flag?: boolean) => {
    try {
      const { data, err } = await AiApi.putAiSettingsByField("onboarded", true);
      if (err || !data) {
        throw err || new Error("Failed to complete onboarding");
      }

      const { data: welcomeData, err: welcomeErr } =
        await AiApi.putAiSettingsByField("welcomeCompleted", false);
      if (welcomeErr || !welcomeData) {
        throw welcomeErr || new Error("Failed to complete welcome lock");
      }

      const { data: settingsData } = await SettingsApi.get();
      if (settingsData?.settings) {
        setSettings(settingsData.settings);
      }

      await queryClient.invalidateQueries({ queryKey: ["getAiStatus"] });
      setOnboarded(true);

      if (flag) {
        setTrained(true);
        history.replace("/welcome");
        setOnboarding({
          loading: false,
          fetchingWebsite: false,
          teachingAgent: false,
          creatingAgent: false,
          error: false,
          message: "",
        });
      }
    } catch (e) {
      setFormError("common.tryItLater");
      setOnboarding({
        loading: false,
        error: true,
      });
    }
  };

  const handleSkipToStepTwo = () => {
    setFormError(null);
    setOnboarding({
      step: 2,
      loading: false,
      teachingAgent: false,
      creatingAgent: false,
      fetchingWebsite: false,
      error: false,
      message: "",
    });
  };

  const getMessageId = (step: number) => {
    switch (step) {
      case 1:
        return "ai.onboarding.desc";
      case 2:
        return "ai.onboarding.company.description";
      default:
        return "ai.onboarding.desc";
    }
  };

  return (
    <div
      className={`w-100 flex align-items-center justify-content-center h-100 ${styles.mainBG}`}
    >
      <div className={styles.content}>
        <div className="logo mb-2" />

        <h1 className={headerStyles.pageTitle}>
          <FormattedMessage id="ai.onboarding.title" />
        </h1>
        <p className="fs-3 color-step-700 mb-3">
          <FormattedMessage id={getMessageId(step)} />
        </p>

        <form className={styles.authForm} onSubmit={handleForm}>
          <AnimatePresence mode="wait">
            {(() => {
              switch (step) {
                case 1:
                  return (
                    <motion.div
                      key="step-1"
                      variants={stepVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className={styles.inputHolder}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className={`${styles.sectionLabel} mb-0`}>
                          <FormattedMessage id="ai.sources.website" />
                        </label>
                      </div>

                      <div className={styles.inputWrapper}>
                        <IconWorld size={18} className={styles.inputIcon} />
                        <input
                          type="text"
                          className={`form-control no-focus ${styles.inputWithIcon}`}
                          value={website}
                          onChange={(e) =>
                            setOnboarding({ website: e.target.value })
                          }
                          placeholder="https://website.com"
                          autoComplete="off"
                          required
                          disabled={loading}
                        />
                      </div>
                      {error && (
                        <span
                          className="form-text mt-2 d-block fs-3 fw-bold"
                          style={{
                            color: "var(--visito-color-danger)",
                          }}
                        >
                          <FormattedMessage
                            id={formError ? formError : "ai.onboarding.error"}
                          />
                        </span>
                      )}
                      <span
                        className="form-text mt-2 d-block fs-2"
                        style={{
                          color: formError
                            ? "var(--visito-color-danger)"
                            : "var(--ion-color-step-600)",
                        }}
                      >
                        <FormattedMessage
                          id={
                            formError ? formError : "ai.onboarding.sourceNotice"
                          }
                        />
                      </span>
                    </motion.div>
                  );
                case 2:
                  return (
                    <motion.div
                      key="step-2"
                      variants={stepVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      <div className={styles.inputHolder}>
                        <label className={styles.sectionLabel}>
                          <FormattedMessage id="ai.onboarding.companyName.label" />
                        </label>
                        <input
                          type="text"
                          className="form-control no-focus"
                          value={companyName}
                          onChange={(e) =>
                            setOnboarding({ companyName: e.target.value })
                          }
                          placeholder={intl.formatMessage({
                            id: "ai.onboarding.companyName.placeholder",
                          })}
                          autoComplete="organization"
                          required
                          disabled={loading}
                        />
                      </div>

                      <div className={`${styles.inputHolder} mt-3`}>
                        <label className={styles.sectionLabel}>
                          <FormattedMessage id="ai.onboarding.aboutCompany.label" />
                        </label>
                        <textarea
                          className={`form-control no-focus ${styles.aboutTextarea}`}
                          value={aboutCompany}
                          onChange={(e) =>
                            setOnboarding({ aboutCompany: e.target.value })
                          }
                          placeholder={intl.formatMessage({
                            id: "ai.onboarding.aboutCompany.placeholder",
                          })}
                          rows={6}
                          required
                          disabled={loading}
                        />
                      </div>

                      <span className={`form-text mt-2 d-block fs-2 ${styles.helperText}`}>
                        <FormattedMessage id="ai.onboarding.company.contextHelp" />
                      </span>
                      {formError ? (
                        <span
                          className="form-text mt-2 d-block fs-3 fw-bold"
                          style={{ color: "var(--visito-color-danger)" }}
                        >
                          <FormattedMessage id={formError} />
                        </span>
                      ) : null}
                    </motion.div>
                  );
                default:
                  return null;
              }
            })()}
          </AnimatePresence>

          <button
            className={`btn btn-visito-primary w-100 mt-4 ${styles.primaryBtn}`}
            type="submit"
            disabled={
              loading ||
              (step === 2 &&
                (!companyName.trim().length || !aboutCompany.trim().length))
            }
          >
            {loading ? (
              <Spinner animation="border" role="status" size="sm">
                <span className="visually-hidden">
                  <FormattedMessage id="common.loading" />
                  ...
                </span>
              </Spinner>
            ) : (
              <>
                {step === 2 && (
                  <IconSparkles size={18} style={{ marginRight: "8px" }} />
                )}
                <FormattedMessage
                  id={
                    [1].includes(step) ? "common.next" : "ai.onboarding.cta"
                  }
                />
              </>
            )}
          </button>
          {step === 1 ? (
            <button
              className={styles.skipButton}
              type="button"
              onClick={handleSkipToStepTwo}
              disabled={loading}
            >
              <FormattedMessage id="ai.onboarding.cta.skip" />
            </button>
          ) : null}
        </form>
      </div>
    </div>
  );
}
